"use client";

/**
 * Agenda Day Card
 * The Agenda layout's "One day at a time" mode: a white card with the Fetch-style day stepper, the
 * day's summary, and its items as rows. Live pills only light up while today is showing.
 */

import type React from "react";

import { useScheduleLiveMarkers } from "../../../../_hooks/useScheduleLiveMarkers";
import type { AgendaDay } from "../../../../_logic/utils/schedule-agenda";
import { ScheduleDayStepper } from "./ScheduleDayStepper";
import { ScheduleRow, type ScheduleRowConfig } from "./ScheduleRow";

const NO_ITEMS: AgendaDay["items"] = [];

interface AgendaDayCardProps {
  days: AgendaDay[];
  selectedDay: AgendaDay;
  onSelectDate: (date: string) => void;
  /** Today in the event's zone, or null before it resolves on the client. */
  todayStr: string | null;
  /** Row render context; its `surface` should be "card". */
  rowConfig: ScheduleRowConfig;
  timeZone: string | null;
}

/**
 * Renders the one-day agenda card.
 *
 * @param props - See AgendaDayCardProps.
 * @returns The card element.
 */
export function AgendaDayCard({
  days,
  selectedDay,
  onSelectDate,
  todayStr,
  rowConfig,
  timeZone,
}: AgendaDayCardProps): React.ReactElement {
  const isToday = selectedDay.date === todayStr;
  const { liveItemIds, upcomingMinutesById } = useScheduleLiveMarkers(isToday ? selectedDay.items : NO_ITEMS, timeZone);
  const dates = days.map((day) => day.date);

  return (
    <div className="flex w-full flex-col gap-5 rounded-2xl border border-gray-200 bg-white px-5 pb-2 pt-5 sm:px-7 sm:pt-7">
      <div className="flex flex-col gap-3">
        <ScheduleDayStepper days={dates} selectedDate={selectedDay.date} todayStr={todayStr} onChange={onSelectDate} />
        {selectedDay.summary && (
          <p className="whitespace-pre-line text-base font-medium text-gray-700">{selectedDay.summary}</p>
        )}
      </div>
      {selectedDay.items.length > 0 ? (
        <ul className="flex list-none flex-col divide-y divide-gray-100 border-t border-gray-100">
          {selectedDay.items.map((item) => (
            <li key={item.id}>
              <ScheduleRow
                config={rowConfig}
                item={item}
                isLive={liveItemIds.has(item.id)}
                startsInMinutes={upcomingMinutesById.get(item.id)}
              />
            </li>
          ))}
        </ul>
      ) : (
        !selectedDay.summary && (
          <p className="pb-5 text-sm font-medium text-gray-500">Nothing scheduled for this day.</p>
        )
      )}
    </div>
  );
}
